import { gql } from '@apollo/client';
import classNames from 'classnames';
import React from 'react';
import SVGIcon from '../components/SVGIcon';
import type { PostIcon_PostFragment } from '../graphql/generated/graphqlTypes';
import { PostType } from '../graphql/generated/graphqlTypes';
import draftIcon from '../icons/drafts.svg';
import prefilledDraftIcon from '../icons/prefilled/drafts.svg';
import prefilledPostIcon from '../icons/prefilled/post.svg';
import postIcon from '../icons/post.svg';
import templateFilledIcon from '../icons/prefilled/template.svg';
import templateBaseIcon from '../icons/prefilled/templateBase.svg';
import templateIcon from '../icons/template.svg';
import templateDraftIcon from '../icons/prefilled/templateDraft.svg';
import postArchivedIcon from '../icons/prefilled/postArchived.svg';
import SeriesIcon from '../series/SeriesIcon';
import getExternalIcon from './getExternalIcon';
import styles from './PostIcon.module.scss';

const POST_ICON_POST_FRAGMENT = gql`
  fragment PostIcon_post on RootPostFields {
    id
    series {
      id
    }
    ... on SlimPostFields {
      archivedAt
      publishedAt
      externalPost {
        id
        platform
      }
      type
    }
  }
`;

type PostState = {
  type: PostType;
  draft: boolean;
  archived: boolean;
};

export const getStateIcon = (
  { type, draft, archived }: PostState,
  prefilled = false,
) => {
  switch (type) {
    case PostType.Post: {
      if (draft) return prefilled ? prefilledDraftIcon : draftIcon;
      if (archived) return prefilled ? postArchivedIcon : postIcon;
      return prefilled ? prefilledPostIcon : postIcon;
    }
    case PostType.Template: {
      if (draft) return templateDraftIcon;
      if (archived) return prefilled ? templateBaseIcon : templateIcon;
      return prefilled ? templateFilledIcon : templateIcon;
    }
    default: {
      return prefilled ? prefilledPostIcon : postIcon;
    }
  }
};

export const getPostState = (
  post?: PostIcon_PostFragment | null,
): PostState => {
  if (post) {
    const type = 'type' in post ? post.type : PostType.Post;
    const isDraft = 'publishedAt' in post ? post.publishedAt == null : false;
    const isArchived = 'archivedAt' in post ? post.archivedAt != null : false;

    return {
      type,
      draft: isDraft,
      archived: isArchived,
    };
  }

  return { type: PostType.Post, draft: false, archived: false };
};

export const getPostIcon = (post: PostIcon_PostFragment, prefilled?: boolean) =>
  getStateIcon(getPostState(post), prefilled);

interface PostIconForStateProps {
  state: PostState;
  externalIcon?: string;
  prefilled?: boolean;
  bordered?: boolean;
}

export const PostIconForState = ({
  state,
  externalIcon,
  prefilled,
  bordered,
}: PostIconForStateProps) => {
  return (
    <div
      className={classNames(
        styles.container,
        styles[state.type.toLowerCase()],
        {
          [styles.archived]: state.archived,
          [styles.draft]: state.draft,
          [styles.bordered]: bordered,
        },
      )}
    >
      <SVGIcon>{getStateIcon(state, prefilled)}</SVGIcon>
      {externalIcon && (
        <div className={styles.externalIconContainer}>
          <div className={styles.externalIconInner}>
            <SVGIcon size={8}>{externalIcon}</SVGIcon>
          </div>
        </div>
      )}
    </div>
  );
};

interface PostIconProps {
  post?: PostIcon_PostFragment;
  bordered?: boolean;
}

const PostIcon = ({ post, bordered }: PostIconProps) => {
  const isDraft =
    post && 'publishedAt' in post ? post.publishedAt == null : false;

  const postState = getPostState(post);

  if (post?.series) {
    return <SeriesIcon archived={postState.archived} />;
  }

  const externalIcon =
    post &&
    'externalPost' in post &&
    post.externalPost &&
    getExternalIcon(post.externalPost.platform);

  return (
    <PostIconForState
      state={postState}
      externalIcon={externalIcon || undefined}
      prefilled={!isDraft}
      bordered={bordered}
    />
  );
};

PostIcon.fragments = {
  post: POST_ICON_POST_FRAGMENT,
};

export default PostIcon;
